import { useState, useEffect, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";
import "bootstrap/dist/css/bootstrap.min.css";

// ─── Supabase ────────────────────────────────────────────────────────────────
const SUPABASE_URL = "https://lpmrtazyoenskpvxiksi.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxwbXJ0YXp5b2Vuc2twdnhpa3NpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjQ2NTc4OTYsImV4cCI6MjA4MDIzMzg5Nn0.52Nvjvu5GQKcFGGvSXxXvR23nlNF8LNMxu8A2i8UZDI";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    detectSessionInUrl: false,
    persistSession: false,
    autoRefreshToken: false,
    lock: (name, acquireTimeout, fn) => fn()
  }
});

// ─── Logo ────────────────────────────────────────────────────────────────────
const LOGO_URL = "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ-cXFuavrc6SQ1s7DJvs55FQ-BF0bqYSM-iw&s";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "";

const StatusBadge = ({ status }) => {
  const map = {
    Submitted: "badge-submitted",
    "In Progress": "badge-in-progress",
    Resolved: "badge-resolved",
    Pending: "badge-pending",
    Found: "badge-found",
    Returned: "badge-success",
    Registered: "badge-success",
    Confirmed: "badge-success",
    Cancelled: "badge-error",
    Lost: "badge-lost",
  };
  const cls = map[status] || "badge-info";
  return <span className={`badge ${cls}`}>{status}</span>;
};

const UrgencyDot = ({ urgency }) => {
  const colors = { Low: "var(--success)", Medium: "var(--warning)", High: "var(--error)" };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: colors[urgency] || "#666" }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors[urgency] || "#666" }} />
      {urgency}
    </span>
  );
};

const Avatar = ({ name, size = 36, bg = "var(--primary-green)" }) => (
  <div className="user-avatar" style={{ width: size, height: size, background: bg, fontSize: size * 0.4, color: "white" }}>
    {(name || "U")[0].toUpperCase()}
  </div>
);

const EmptyState = ({ icon, text }) => (
  <div style={{ textAlign: "center", padding: "48px 24px", color: "var(--text-muted)" }}>
    <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
    <p style={{ fontWeight: 500, fontSize: "var(--text-base)", margin: 0 }}>{text}</p>
  </div>
);

const StatCard = ({ icon, label, value, bgClass = "" }) => (
  <div className="stat-card">
    <div className={`stat-icon ${bgClass}`}>{icon}</div>
    <div>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  </div>
);

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar({ active, setActive, role, profile, onLogout, open, onClose }) {
  const studentNav = [
    { id: "overview", icon: "📊", label: "Dashboard" },
    { id: "complaints", icon: "📋", label: "Complaints" },
    { id: "lost-found", icon: "🔍", label: "Lost & Found" },
    { id: "volunteer", icon: "🤝", label: "Volunteer" },
    { id: "notifications", icon: "🔔", label: "Notifications" },
  ];
  const adminNav = [
    { id: "admin-overview", icon: "📊", label: "Overview" },
    { id: "admin-complaints", icon: "📋", label: "Complaints" },
    { id: "admin-lost-found", icon: "🔍", label: "Lost & Found" },
    { id: "admin-volunteers", icon: "🤝", label: "Volunteers" },
    { id: "admin-users", icon: "👥", label: "Users" },
  ];
  const nav = role === "admin" ? adminNav : studentNav;

  return (
    <>
      <div className={`modal-overlay ${open ? 'd-flex' : ''}`} style={{ display: open ? "flex" : "none", zIndex: 90 }} onClick={onClose} />
      <div className={`sidebar ${open ? "open" : ""}`}>
        <div className="sidebar-brand">
          <img src={LOGO_URL} alt="Logo" style={{ width: 32, height: 32, borderRadius: 8 }} onError={e => e.target.style.display = "none"} />
          <span>Campus Portal</span>
        </div>
        <div className="nav-menu">
          {nav.map(item => (
            <button key={item.id} className={`nav-item ${active === item.id ? "active" : ""}`}
              onClick={() => { setActive(item.id); onClose(); }}>
              <span className="nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
        <div className="sidebar-footer">
          <div className="user-profile mb-3">
            <Avatar name={profile?.name || profile?.email} bg={role === "admin" ? "var(--info)" : "var(--primary-green)"} />
            <div className="user-info">
              <div className="user-name">{profile?.name || profile?.email?.split("@")[0]}</div>
              <div className="user-role">{role}</div>
            </div>
          </div>
          <button className="nav-item" onClick={onLogout} style={{ color: "var(--error)", padding: '8px 0' }}>
            <span className="nav-icon">🚪</span>
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </>
  );
}

// ─── TopBar ───────────────────────────────────────────────────────────────────
function TopBar({ title, unread, onNotif }) {
  return (
    <div className="topbar">
      <div>
        <h5 className="page-title">{title}</h5>
        <p className="page-date">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
      </div>
      <div className="d-flex align-items-center gap-3">
        <div style={{ position: "relative" }}>
          <button className="notif-bell" onClick={onNotif} style={{ border: "none", cursor: "pointer", background: "var(--gray-100)" }}>
            🔔
          </button>
          {unread > 0 && (
            <span style={{ position: "absolute", top: -4, right: -4, background: "var(--error)", color: "white", width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: "bold", border: "2px solid white" }}>
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </div>
        <img src={LOGO_URL} alt="logo" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover" }} />
      </div>
    </div>
  );
}

// ─── Login ────────────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [role, setRole] = useState("student");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isSignup, setIsSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      if (isSignup) {
        const { error: err } = await supabase.auth.signUp({ email, password, options: { data: { name, role } } });
        if (err) throw err;
        setError("✅ Account created! Please log in.");
        setIsSignup(false);
      } else {
        const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
        if (err) throw err;
        const { data: profile } = await supabase.from("users").select("*").eq("id", data.user.id).maybeSingle();
        onLogin(data.user, profile);
      }
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-wave" />
      <div className="login-card">
        <div className="login-logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
          Saylani
        </div>
        <h1 className="login-title">Saylani Portal</h1>
        <p className="login-subtitle">Student & Administrator Access</p>
        
        <div className="role-toggle">
          <button type="button" className={`role-pill ${role === "student" ? "active" : ""}`} onClick={() => setRole("student")}>Student</button>
          <button type="button" className={`role-pill ${role === "admin" ? "active" : ""}`} onClick={() => setRole("admin")}>Admin</button>
        </div>
        
        {error && (
          <div className="mb-3 p-3 rounded" style={{ fontSize: 13, background: error.includes("✅") ? "var(--success)" : "var(--error)", color: "white", fontWeight: 500 }}>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          {isSignup && (
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="form-control" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" required />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-control" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required />
          </div>
          <div className="form-group" style={{ marginBottom: 24 }}>
            <label className="form-label">Password</label>
            <input className="form-control" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
          </div>
          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? "Please wait..." : (isSignup ? "Create Account" : "Log In")}
          </button>
        </form>
        
        <div className="text-center mt-4 pt-2">
          <button onClick={() => { setIsSignup(!isSignup); setError(""); }}
            style={{ background: "none", border: "none", color: "var(--text-secondary)", fontWeight: 500, cursor: "pointer", fontSize: 13 }}>
            🔗 {isSignup ? "Sign in with account" : "Don't have an account? Sign Up"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Complaint Form ───────────────────────────────────────────────────────────
function ComplaintForm({ userId, onSuccess, onCancel }) {
  const [form, setForm] = useState({ title: "", category: "Internet", description: "", location: "", urgency: "Medium" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const cats = ["Internet", "Electricity", "Water", "Maintenance", "Security", "Sanitation", "Other"];

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { error: err } = await supabase.from("complaints").insert({ user_id: userId, ...form });
      if (err) throw err;
      onSuccess();
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  return (
    <form onSubmit={submit}>
      {error && <div className="mb-3 p-3 rounded" style={{ background: "var(--error)", color: "white", fontSize: 13, fontWeight: 500 }}>{error}</div>}
      
      <div className="row mb-3">
        <div className="col-12 col-md-6 mb-3">
          <label className="form-label">Complaint Title</label>
          <input className="form-control" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Brief title" required />
        </div>
        <div className="col-12 col-md-6 mb-3">
          <label className="form-label">Category</label>
          <select className="form-select form-control" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
            {cats.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      
      <div className="form-group mb-3">
        <label className="form-label">Description</label>
        <textarea className="form-control" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe the issue..." required />
      </div>
      
      <div className="row mb-4">
        <div className="col-12 col-md-6 mb-3">
          <label className="form-label">Location</label>
          <input className="form-control" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g., Block A, Room 203" />
        </div>
        <div className="col-12 col-md-6 mb-3">
          <label className="form-label">Urgency</label>
          <div className="radio-cards">
            {["Low", "Medium", "High"].map(u => (
              <button key={u} type="button" className={`radio-card ${form.urgency === u ? 'active-' + u.toLowerCase() : ''}`} onClick={() => setForm({ ...form, urgency: u })}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'currentColor', marginRight: 6 }} />
                {u}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="d-flex justify-content-center gap-3">
        {onCancel && <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn btn-primary" disabled={loading} style={{ padding: '12px 32px' }}>
          {loading ? "Submitting..." : "Submit Complaint"}
        </button>
      </div>
    </form>
  );
}

// ─── Lost Found Form ──────────────────────────────────────────────────────────
function LostFoundForm({ userId, onSuccess, onCancel }) {
  const [form, setForm] = useState({ type: "Lost", title: "", description: "", location: "" });
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      let image_url = null;
      if (imageFile) {
        const fname = `${userId}/${Date.now()}-${imageFile.name}`;
        const { error: upErr } = await supabase.storage.from("lost-found-images").upload(fname, imageFile);
        if (!upErr) {
          const { data: urlData } = supabase.storage.from("lost-found-images").getPublicUrl(fname);
          image_url = urlData?.publicUrl;
        }
      }
      const { error: err } = await supabase.from("lost_found_items").insert({ user_id: userId, ...form, image_url });
      if (err) throw err;
      onSuccess();
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  return (
    <form onSubmit={submit}>
      {error && <div className="mb-3 p-3 rounded" style={{ background: "var(--error)", color: "white", fontSize: 13, fontWeight: 500 }}>{error}</div>}
      <div className="form-group mb-3">
        <label className="form-label">Type</label>
        <div className="radio-cards">
          {["Lost", "Found"].map(t => (
            <button key={t} type="button" className={`radio-card ${form.type === t ? 'active-' + t.toLowerCase() : ''}`} onClick={() => setForm({ ...form, type: t })}
              style={{
                borderColor: form.type === t ? (t === "Lost" ? "var(--error)" : "var(--success)") : "var(--gray-200)",
                background: form.type === t ? (t === "Lost" ? "#FFEBEE" : "#E8F5E9") : "white",
                color: form.type === t ? (t === "Lost" ? "var(--error)" : "var(--success)") : "var(--text-secondary)"
              }}>
              {t === "Lost" ? "🔴 Lost Item" : "🟢 Found Item"}
            </button>
          ))}
        </div>
      </div>
      <div className="form-group mb-3">
        <label className="form-label">Item Title</label>
        <input className="form-control" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g., Blue Backpack" required />
      </div>
      <div className="form-group mb-3">
        <label className="form-label">Description</label>
        <textarea className="form-control" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Color, brand, features..." required />
      </div>
      <div className="row mb-4">
        <div className="col-12 col-md-6 mb-3">
          <label className="form-label">Location</label>
          <input className="form-control" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Where lost/found?" />
        </div>
        <div className="col-12 col-md-6 mb-3">
          <label className="form-label">Photo (optional)</label>
          <input className="form-control" type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} style={{ padding: "10px" }} />
        </div>
      </div>
      <div className="d-flex justify-content-end gap-3">
        {onCancel && <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn btn-primary" disabled={loading} style={{ padding: '12px 32px' }}>
          {loading ? "Posting..." : "🔍 Post Item"}
        </button>
      </div>
    </form>
  );
}

// ─── Volunteer Form ───────────────────────────────────────────────────────────
function VolunteerForm({ userId, onSuccess, onCancel }) {
  const [form, setForm] = useState({ name: "", event_name: "", availability: "", skills: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { error: err } = await supabase.from("volunteers").insert({ user_id: userId, ...form });
      if (err) throw err;
      onSuccess();
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  return (
    <form onSubmit={submit}>
      {error && <div className="mb-3 p-3 rounded" style={{ background: "var(--error)", color: "white", fontSize: 13, fontWeight: 500 }}>{error}</div>}
      <div className="row mb-3">
        <div className="col-12 col-md-6 mb-3">
          <label className="form-label">Your Name</label>
          <input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" required />
        </div>
        <div className="col-12 col-md-6 mb-3">
          <label className="form-label">Event Name</label>
          <input className="form-control" value={form.event_name} onChange={e => setForm({ ...form, event_name: e.target.value })} placeholder="e.g., Community Cleanup" required />
        </div>
      </div>
      <div className="form-group mb-3">
        <label className="form-label">Availability</label>
        <input className="form-control" value={form.availability} onChange={e => setForm({ ...form, availability: e.target.value })} placeholder="e.g., Weekends, 9am-5pm" required />
      </div>
      <div className="form-group mb-4">
        <label className="form-label">Skills / Notes</label>
        <textarea className="form-control" rows={3} value={form.skills} onChange={e => setForm({ ...form, skills: e.target.value })} placeholder="Any relevant skills..." />
      </div>
      <div className="d-flex justify-content-end gap-3">
        {onCancel && <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn btn-success" disabled={loading} style={{ padding: '12px 32px' }}>
          {loading ? "Registering..." : "🤝 Register"}
        </button>
      </div>
    </form>
  );
}

// ─── Student Dashboard ────────────────────────────────────────────────────────
function StudentDashboard({ user, profile }) {
  const [section, setSection] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [lostFound, setLostFound] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showForm, setShowForm] = useState(null);
  const [loading, setLoading] = useState(true);

  const uid = user.id;
  const unread = notifications.filter(n => !n.is_read).length;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [c, l, v, n] = await Promise.all([
      supabase.from("complaints").select("*").eq("user_id", uid).order("created_at", { ascending: false }),
      supabase.from("lost_found_items").select("*").eq("user_id", uid).order("created_at", { ascending: false }),
      supabase.from("volunteers").select("*").eq("user_id", uid).order("created_at", { ascending: false }),
      supabase.from("notifications").select("*").eq("user_id", uid).order("created_at", { ascending: false }),
    ]);
    setComplaints(c.data || []);
    setLostFound(l.data || []);
    setVolunteers(v.data || []);
    setNotifications(n.data || []);
    setLoading(false);
  }, [uid]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const ch = supabase.channel("student-" + uid)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${uid}` }, (payload) => {
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(payload.new.title, { body: payload.new.message });
        }
        fetchAll();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${uid}` }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "complaints", filter: `user_id=eq.${uid}` }, fetchAll)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [fetchAll, uid]);

  const markAllRead = async () => {
    await supabase.from("notifications").update({ is_read: true }).eq("user_id", uid).eq("is_read", false);
    setNotifications(notifications.map(n => ({ ...n, is_read: true })));
  };

  const handleSignOut = () => {
    supabase.auth.signOut().catch(() => {});
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/";
  };

  const titles = { overview: "Dashboard", complaints: "My Complaints", "lost-found": "Lost & Found", volunteer: "Volunteer", notifications: "Notifications" };

  return (
    <div className="layout-wrapper">
      <button className="mobile-toggle" onClick={() => setSidebarOpen(true)}>☰</button>
      <Sidebar active={section} setActive={setSection} role="student" profile={profile || user}
        onLogout={handleSignOut} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="main-content">
        <TopBar title={titles[section] || "Dashboard"} unread={unread} onNotif={() => setSection("notifications")} />
        <div className="content-area">
          {loading ? (
            <div className="text-center py-5">
              <div style={{ fontSize: 40 }}>⏳</div>
              <p style={{ color: "#9e9e9e", fontSize: 14 }}>Loading...</p>
            </div>
          ) : (
            <>
              {/* OVERVIEW */}
              {section === "overview" && (
                <>
                  <p style={{ fontSize: 13, color: "#9e9e9e", marginBottom: 16 }}>
                    Welcome back, <strong style={{ color: "#202124" }}>{profile?.name || user.email.split("@")[0]}</strong> 👋
                  </p>

                  {/* Stats */}
                  <div className="stats-grid">
                    <StatCard icon="📋" label="Total Complaints" value={complaints.length} bgClass="stat-blue" />
                    <StatCard icon="⏳" label="Active Complaints" value={complaints.filter(c => c.status !== "Resolved").length} bgClass="stat-red" />
                    <StatCard icon="✅" label="Items Matched" value={lostFound.filter(l => l.status !== "Pending").length} bgClass="stat-green" />
                    <StatCard icon="📅" label="Events Registered" value={volunteers.length} bgClass="stat-orange" />
                  </div>

                  {/* Recent Activity + Right Panel */}
                  <div className="row">
                    <div className="col-12 col-lg-8">
                      <div className="panel">
                        <div className="panel-header">
                          <span className="panel-title">
                            <div className="panel-title-icon">📌</div>
                            Recent Activity
                          </span>
                          <div className="action-buttons">
                            <button className="btn btn-success btn-sm" onClick={() => { setSection("lost-found"); setShowForm("lostfound"); }}>🔍 Report Lost Item</button>
                            <button className="btn btn-warning btn-sm" onClick={() => { setSection("complaints"); setShowForm("complaint"); }}>📋 Submit Complaint</button>
                            <button className="btn btn-danger btn-sm" onClick={() => setSection("volunteer")}>🤝 Register for Event</button>
                          </div>
                        </div>
                        {complaints.length === 0 ? <EmptyState icon="📋" text="No activity yet" /> : (
                          <div className="table-container">
                            <table className="data-table">
                              <thead><tr><th>Item / Complaint</th><th>Type</th><th>Status</th></tr></thead>
                              <tbody>
                                {complaints.slice(0, 6).map(c => (
                                  <tr key={c.id}>
                                    <td style={{ fontWeight: 600 }}>{c.title}</td>
                                    <td style={{ fontSize: 12, color: "#9e9e9e" }}>{c.category}</td>
                                    <td><StatusBadge status={c.status} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-lg-4">
                      {/* Notifications */}
                      <div className="notif-panel mb-4">
                        <div className="notif-header">
                          <span className="panel-title" style={{ color: "white" }}>
                            <div className="notif-bell">🔔</div> Notifications
                          </span>
                          {unread > 0 && <button onClick={markAllRead} style={{ fontSize: 11, color: "var(--info)", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Mark all read</button>}
                        </div>
                        <div style={{ maxHeight: 200, overflowY: "auto" }}>
                          {notifications.length === 0 ? <EmptyState icon="🔕" text="No notifications" /> :
                            notifications.slice(0, 5).map(n => (
                              <div key={n.id} className={`notif-item ${!n.is_read ? "unread" : ""}`}>
                                <div className="notif-avatar" style={{ background: n.type === "success" ? "var(--success)" : "rgba(255,255,255,0.2)" }}>
                                  {n.type === "success" ? "✅" : "ℹ️"}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div className="notif-title">{n.title}</div>
                                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>{n.message?.slice(0, 50)}...</div>
                                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>{fmtTime(n.created_at)}</div>
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className="panel">
                        <div className="panel-header"><span className="panel-title">⚡ Quick Actions</span></div>
                        <div className="panel-body">
                          <button className="activity-item w-100 text-left" style={{ border: 'none', background: 'var(--bg-light)', padding: 12, display: 'flex' }} onClick={() => { setSection("complaints"); setShowForm("complaint"); }}>
                            <div className="activity-icon" style={{ background: "var(--bg-blue)" }}>💬</div>
                            <div style={{ marginLeft: 12, textAlign: 'left' }}><div style={{ fontSize: 14, fontWeight: 500 }}>Submit Complaint</div><div style={{ fontSize: 12, color: "var(--text-muted)" }}>Report Saylani issues</div></div>
                          </button>
                          <button className="activity-item w-100 text-left" style={{ border: 'none', background: 'var(--bg-light)', padding: 12, display: 'flex' }} onClick={() => { setSection("lost-found"); setShowForm("lostfound"); }}>
                            <div className="activity-icon" style={{ background: "var(--bg-gray)", color: "var(--warning)" }}>🔍</div>
                            <div style={{ marginLeft: 12, textAlign: 'left' }}><div style={{ fontSize: 14, fontWeight: 500 }}>Post Lost/Found</div><div style={{ fontSize: 12, color: "var(--text-muted)" }}>Report or find items</div></div>
                          </button>
                          <button className="activity-item w-100 text-left" style={{ border: 'none', background: 'var(--bg-light)', padding: 12, display: 'flex' }} onClick={() => { setSection("volunteer"); setShowForm("volunteer"); }}>
                            <div className="activity-icon" style={{ background: "var(--bg-green)" }}>🤝</div>
                            <div style={{ marginLeft: 12, textAlign: 'left' }}><div style={{ fontSize: 14, fontWeight: 500 }}>Register as Volunteer</div><div style={{ fontSize: 12, color: "var(--text-muted)" }}>Join Saylani events</div></div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* COMPLAINTS */}
              {section === "complaints" && (
                <div className="panel">
                  <div className="panel-header">
                    <span className="panel-title">📋 My Complaints ({complaints.length})</span>
                    {showForm !== "complaint" && <button className="btn btn-success btn-sm" onClick={() => setShowForm("complaint")}>+ New Complaint</button>}
                  </div>
                  {showForm === "complaint" ? (
                    <div className="panel-body">
                      <h6 style={{ fontWeight: 700, marginBottom: 18 }}>Submit New Complaint</h6>
                      <ComplaintForm userId={uid} onSuccess={() => { setShowForm(null); fetchAll(); }} onCancel={() => setShowForm(null)} />
                    </div>
                  ) : complaints.length === 0 ? <EmptyState icon="📋" text="No complaints yet" /> : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead><tr><th>#</th><th>Title</th><th>Category</th><th>Location</th><th>Urgency</th><th>Status</th><th>Admin Note</th><th>Date</th></tr></thead>
                        <tbody>
                          {complaints.map((c, i) => (
                            <tr key={c.id}>
                              <td style={{ color: "var(--text-muted)" }}>S0{i + 1}</td>
                              <td style={{ fontWeight: 600 }}>{c.title}</td>
                              <td>{c.category}</td>
                              <td style={{ color: "var(--text-muted)", fontSize: 13 }}>{c.location || "—"}</td>
                              <td><UrgencyDot urgency={c.urgency} /></td>
                              <td><StatusBadge status={c.status} /></td>
                              <td style={{ fontSize: 13, color: "var(--text-muted)", maxWidth: 150 }}>{c.admin_note || "—"}</td>
                              <td style={{ fontSize: 13, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{fmtDate(c.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* LOST & FOUND */}
              {section === "lost-found" && (
                <div className="panel">
                  <div className="panel-header">
                    <span className="panel-title">🔍 My Lost & Found ({lostFound.length})</span>
                    {showForm !== "lostfound" && <button className="btn btn-success btn-sm" onClick={() => setShowForm("lostfound")}>+ Post Item</button>}
                  </div>
                  {showForm === "lostfound" ? (
                    <div className="panel-body">
                      <LostFoundForm userId={uid} onSuccess={() => { setShowForm(null); fetchAll(); }} onCancel={() => setShowForm(null)} />
                    </div>
                  ) : lostFound.length === 0 ? <EmptyState icon="🔍" text="No posts yet" /> : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Title</th>
                            <th>Description</th>
                            <th>Location</th>
                            <th>Status</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lostFound.map((item) => (
                            <tr key={item.id}>
                              <td><StatusBadge status={item.type} /></td>
                              <td style={{ fontWeight: 600 }}>{item.title}</td>
                              <td style={{ fontSize: 13, color: "var(--text-muted)", maxWidth: 200 }}>{item.description?.slice(0, 60)}...</td>
                              <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{item.location || "—"}</td>
                              <td><StatusBadge status={item.status} /></td>
                              <td style={{ fontSize: 13, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{fmtDate(item.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* VOLUNTEER */}
              {section === "volunteer" && (
                <div className="panel">
                  <div className="panel-header">
                    <span className="panel-title">🤝 My Registrations ({volunteers.length})</span>
                    {showForm !== "volunteer" && <button className="btn btn-success btn-sm" onClick={() => setShowForm("volunteer")}>+ Register</button>}
                  </div>
                  {showForm === "volunteer" ? (
                    <div className="panel-body">
                      <VolunteerForm userId={uid} onSuccess={() => { setShowForm(null); fetchAll(); }} onCancel={() => setShowForm(null)} />
                    </div>
                  ) : volunteers.length === 0 ? <EmptyState icon="🤝" text="No registrations yet" /> : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead><tr><th>Name</th><th>Event</th><th>Availability</th><th>Skills</th><th>Status</th><th>Date</th></tr></thead>
                        <tbody>
                          {volunteers.map(v => (
                            <tr key={v.id}>
                              <td style={{ fontWeight: 600 }}>{v.name}</td>
                              <td>{v.event_name}</td>
                              <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{v.availability}</td>
                              <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{v.skills || "—"}</td>
                              <td><StatusBadge status={v.status} /></td>
                              <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{fmtDate(v.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* NOTIFICATIONS */}
              {section === "notifications" && (
                <div className="panel">
                  <div className="panel-header">
                    <span className="panel-title">🔔 All Notifications ({notifications.length})</span>
                    {unread > 0 && <button className="btn btn-outline btn-sm" onClick={markAllRead}>Mark all read</button>}
                  </div>
                  {notifications.length === 0 ? <EmptyState icon="🔕" text="No notifications" /> :
                    <div className="panel-body" style={{ padding: 0 }}>
                      {notifications.map(n => (
                        <div key={n.id} className={`notif-item ${!n.is_read ? "unread" : ""}`} style={{ borderRadius: 0, borderBottom: '1px solid var(--border-color)' }}>
                          <div className="notif-avatar" style={{ background: n.type === "success" ? "var(--success)" : n.type === "warning" ? "var(--warning)" : "var(--primary-blue)" }}>
                            {n.type === "success" ? "✅" : n.type === "warning" ? "⚠️" : "ℹ️"}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div className="notif-title">{n.title}</div>
                            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>{n.message}</div>
                            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{fmtDate(n.created_at)} • {fmtTime(n.created_at)}</div>
                          </div>
                          {!n.is_read && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--primary-blue)", flexShrink: 0, marginTop: 6 }} />}
                        </div>
                      ))}
                    </div>
                  }
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Admin Dashboard ──────────────────────────────────────────────────────────
function AdminDashboard({ user, profile }) {
  const [section, setSection] = useState("admin-overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [lostFound, setLostFound] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selComplaint, setSelComplaint] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterCat, setFilterCat] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [notif, setNotif] = useState({ userId: "", title: "", message: "" });
  const [sending, setSending] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l, v, u, n] = await Promise.all([
        supabase.from("complaints").select("*").order("created_at", { ascending: false }),
        supabase.from("lost_found_items").select("*").order("created_at", { ascending: false }),
        supabase.from("volunteers").select("*").order("created_at", { ascending: false }),
        supabase.from("users").select("*").order("created_at", { ascending: false }),
        supabase.from("notifications").select("*").eq("user_id", user.id).order("created_at", { ascending: false }),
      ]);
      setComplaints(c.data || []);
      setLostFound(l.data || []);
      setVolunteers(v.data || []);
      setUsers(u.data || []);
      setNotifications(n.data || []);
    } catch (err) { console.log(err); }
    setLoading(false);
  }, [user.id]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const ch = supabase.channel("admin-" + user.id)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "complaints" }, (payload) => {
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("New Complaint Submitted", { body: payload.new.title });
        }
        fetchAll();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "complaints" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "lost_found_items" }, fetchAll)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [fetchAll, user.id]);

  const updateComplaint = async (id, status, userId, adminNote) => {
    await supabase.from("complaints").update({ status, admin_note: adminNote, updated_at: new Date().toISOString() }).eq("id", id);
    await supabase.from("notifications").insert({
      user_id: userId, title: "Complaint Status Updated",
      message: `Your complaint is now "${status}".${adminNote ? " Note: " + adminNote : ""}`,
      type: status === "Resolved" ? "success" : "info"
    });
    setShowModal(false); fetchAll();
  };

  const updateLF = async (id, status, userId) => {
    await supabase.from("lost_found_items").update({ status }).eq("id", id);
    await supabase.from("notifications").insert({
      user_id: userId, title: "Lost & Found Update",
      message: `Your item status is now "${status}".`,
      type: status === "Found" || status === "Returned" ? "success" : "info"
    });
    fetchAll();
  };

  const deleteLF = async (id) => {
    if (window.confirm("Delete this item?")) {
      await supabase.from("lost_found_items").delete().eq("id", id);
      fetchAll();
    }
  };

  const sendNotif = async () => {
    if (!notif.userId || !notif.title || !notif.message) return;
    setSending(true);
    await supabase.from("notifications").insert({ user_id: notif.userId, title: notif.title, message: notif.message, type: "info" });
    setNotif({ userId: "", title: "", message: "" });
    setSending(false);
    alert("✅ Notification sent!");
  };

  const handleSignOut = () => {
    supabase.auth.signOut().catch(() => {});
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/";
  };

  const getUserInfo = (userId) => users.find(u => u.id === userId);
  const unread = notifications.filter(n => !n.is_read).length;
  const filteredComplaints = complaints.filter(c => {
    if (filterCat !== "All" && c.category !== filterCat) return false;
    if (filterStatus !== "All" && c.status !== filterStatus) return false;
    return true;
  });

  return (
    <div className="layout-wrapper">
      <button className="mobile-toggle" onClick={() => setSidebarOpen(true)}>☰</button>
      <Sidebar active={section} setActive={setSection} role="admin" profile={profile || user}
        onLogout={handleSignOut} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="main-content">
        <TopBar title={section.replace("admin-", "").replace(/-/g, " & ").replace(/\b\w/g, c => c.toUpperCase())}
          unread={unread} onNotif={() => { }} />
        <div className="content-area">
          {loading ? (
            <div className="text-center py-5">
              <div style={{ fontSize: 40 }}>⏳</div>
              <p style={{ color: "#9e9e9e", fontSize: 14 }}>Loading...</p>
            </div>
          ) : (
            <>
              {/* ADMIN OVERVIEW */}
              {section === "admin-overview" && (
                <>
                  {/* Stats */}
                  <div className="stats-grid">
                    <StatCard icon="👥" label="Total Users" value={users.length} bgClass="stat-blue" />
                    <StatCard icon="📋" label="Open Complaints" value={complaints.filter(c => c.status !== "Resolved").length} bgClass="stat-orange" />
                    <StatCard icon="🔍" label="Lost Items Pending" value={lostFound.filter(l => l.status === "Pending").length} bgClass="stat-red" />
                    <StatCard icon="🤝" label="Active Volunteers" value={volunteers.filter(v => v.status !== "Cancelled").length} bgClass="stat-green" />
                  </div>

                  <div className="row g-3 mb-3">
                    {/* Progress Bars */}
                    <div className="col-12 col-lg-6">
                    
                      <div className="panel">
                        <div className="panel-header">
                          <span className="panel-title">📊 Complaints Management</span>
                          <button className="btn btn-primary btn-sm" onClick={() => setSection("admin-complaints")}>View All</button>
                        </div>
                        <div className="panel-body">
                          {["Submitted", "In Progress", "Resolved"].map(s => {
                            const count = complaints.filter(c => c.status === s).length;
                            const pct = complaints.length ? Math.round((count / complaints.length) * 100) : 0;
                            const colors = { Submitted: "var(--primary-blue)", "In Progress": "var(--warning)", Resolved: "var(--success)" };
                            return (
                              <div key={s} className="mb-3">
                                <div className="d-flex justify-content-between mb-2" style={{ fontSize: 13, fontWeight: 600 }}>
                                  <span style={{ color: "var(--text-secondary)" }}>{s}</span>
                                  <span style={{ color: colors[s] }}>{count}</span>
                                </div>
                                <div style={{ height: 8, background: "var(--gray-200)", borderRadius: 4, overflow: "hidden" }}>
                                  <div style={{ height: "100%", width: pct + "%", background: colors[s], borderRadius: 4, transition: "width 0.5s" }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Recent Complaints */}
                    <div className="col-12 col-lg-6">
                      <div className="panel">
                        <div className="panel-header"><span className="panel-title">🕐 Recent Complaints</span></div>
                        {complaints.length === 0 ? <EmptyState icon="📋" text="No complaints yet" /> : (
                          <div className="table-container">
                            <table className="data-table">
                              <thead><tr><th>ID</th><th>Item</th><th>Category</th><th>Status</th></tr></thead>
                              <tbody>
                                {complaints.slice(0, 5).map((c, i) => (
                                  <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => { setSelComplaint(c); setShowModal(true); }}>
                                    <td style={{ color: "var(--text-muted)" }}>S0{i + 1}</td>
                                    <td style={{ fontWeight: 600 }}>{c.title}</td>
                                    <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{c.category}</td>
                                    <td><StatusBadge status={c.status} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Send Notification */}
                  <div className="panel">
                    <div className="panel-header"><span className="panel-title">📨 Send Notification to Student</span></div>
                    <div className="panel-body">
                      <div className="row align-items-end mb-3">
                        <div className="col-12 col-md-3 mb-3">
                          <label className="form-label">Select Student</label>
                          <select className="form-select form-control" value={notif.userId} onChange={e => setNotif({ ...notif, userId: e.target.value })}>
                            <option value="">Choose student...</option>
                            {users.filter(u => u.role === "student").map(u => (
                              <option key={u.id} value={u.id}>{u.name || u.email}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12 col-md-3 mb-3">
                          <label className="form-label">Title</label>
                          <input className="form-control" value={notif.title} onChange={e => setNotif({ ...notif, title: e.target.value })} placeholder="Notification title" />
                        </div>
                        <div className="col-12 col-md-4 mb-3">
                          <label className="form-label">Message</label>
                          <input className="form-control" value={notif.message} onChange={e => setNotif({ ...notif, message: e.target.value })} placeholder="Message..." />
                        </div>
                        <div className="col-12 col-md-2 mb-3">
                          <button className="btn btn-primary w-100" onClick={sendNotif} disabled={sending}>
                            {sending ? "Sending..." : "Send 📨"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ADMIN COMPLAINTS */}
              {section === "admin-complaints" && (
                <div className="panel">
                  <div className="panel-header">
                    <span className="panel-title">📋 All Complaints ({filteredComplaints.length})</span>
                    <div className="d-flex gap-2 flex-wrap">
                      <select className="form-select form-control" value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ width: 130, padding: "8px 12px", fontSize: 13 }}>
                        {["All", "Internet", "Electricity", "Water", "Maintenance", "Security", "Sanitation", "Other"].map(c => <option key={c}>{c}</option>)}
                      </select>
                      <select className="form-select form-control" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 130, padding: "8px 12px", fontSize: 13 }}>
                        {["All", "Submitted", "In Progress", "Resolved"].map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  {filteredComplaints.length === 0 ? <EmptyState icon="📋" text="No complaints found" /> : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead><tr><th>ID</th><th>Student</th><th>Title</th><th>Category</th><th>Urgency</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
                        <tbody>
                          {filteredComplaints.map((c, i) => {
                            const u = getUserInfo(c.user_id);
                            return (
                              <tr key={c.id}>
                                <td style={{ color: "var(--text-muted)", fontSize: 13 }}>S0{i + 1}</td>
                                <td>
                                  <div style={{ fontWeight: 500, fontSize: 14 }}>{u?.name || "Unknown"}</div>
                                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{u?.email}</div>
                                </td>
                                <td style={{ fontWeight: 500, fontSize: 14 }}>{c.title}</td>
                                <td style={{ fontSize: 13 }}>{c.category}</td>
                                <td><UrgencyDot urgency={c.urgency} /></td>
                                <td><StatusBadge status={c.status} /></td>
                                <td style={{ fontSize: 13, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{fmtDate(c.created_at)}</td>
                                <td><button className="btn btn-outline btn-sm" onClick={() => { setSelComplaint(c); setShowModal(true); }}>Update</button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ADMIN LOST & FOUND */}
              {section === "admin-lost-found" && (
                <div className="panel">
                  <div className="panel-header"><span className="panel-title">🔍 Lost & Found Items ({lostFound.length})</span></div>
                  {lostFound.length === 0 ? <EmptyState icon="🔍" text="No items posted" /> : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead><tr><th>Type</th><th>Title</th><th>Student</th><th>Location</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead>
                        <tbody>
                          {lostFound.map(item => {
                            const u = getUserInfo(item.user_id);
                            return (
                              <tr key={item.id}>
                                <td><StatusBadge status={item.type} /></td>
                                <td style={{ fontWeight: 500, fontSize: 14 }}>{item.title}</td>
                                <td style={{ fontSize: 13 }}>{u?.name || "Unknown"}</td>
                                <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{item.location || "—"}</td>
                                <td><StatusBadge status={item.status} /></td>
                                <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{fmtDate(item.created_at)}</td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <select className="form-select form-control" value={item.status}
                                      onChange={e => updateLF(item.id, e.target.value, item.user_id)}
                                      style={{ width: 110, padding: "6px 10px", fontSize: 13 }}>
                                      <option>Pending</option><option>Found</option><option>Returned</option>
                                    </select>
                                    <button className="btn btn-outline btn-sm" onClick={() => deleteLF(item.id)}
                                      style={{ background: "#ffebee", color: "var(--error)", border: "none" }}>🗑️</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ADMIN VOLUNTEERS */}
              {section === "admin-volunteers" && (
                <div className="panel">
                  <div className="panel-header"><span className="panel-title">🤝 Volunteer Registrations ({volunteers.length})</span></div>
                  {volunteers.length === 0 ? <EmptyState icon="🤝" text="No registrations yet" /> : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead><tr><th>Name</th><th>Student</th><th>Event</th><th>Availability</th><th>Skills</th><th>Status</th><th>Action</th></tr></thead>
                        <tbody>
                          {volunteers.map(v => {
                            const u = getUserInfo(v.user_id);
                            return (
                              <tr key={v.id}>
                                <td style={{ fontWeight: 500 }}>{v.name}</td>
                                <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{u?.email}</td>
                                <td>{v.event_name}</td>
                                <td style={{ fontSize: 13 }}>{v.availability}</td>
                                <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{v.skills || "—"}</td>
                                <td><StatusBadge status={v.status} /></td>
                                <td>
                                  <select className="form-select form-control" value={v.status}
                                    onChange={async e => { await supabase.from("volunteers").update({ status: e.target.value }).eq("id", v.id); fetchAll(); }}
                                    style={{ width: 120, padding: "6px 10px", fontSize: 13 }}>
                                    <option>Registered</option><option>Confirmed</option><option>Cancelled</option>
                                  </select>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ADMIN USERS */}
              {section === "admin-users" && (
                <div className="panel">
                  <div className="panel-header"><span className="panel-title">👥 All Users ({users.length})</span></div>
                  <div className="table-container">
                    <table className="data-table">
                      <thead><tr><th>User</th><th>Email</th><th>Role</th><th>Joined</th></tr></thead>
                      <tbody>
                        {users.map(u => (
                          <tr key={u.id}>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <Avatar name={u.name || u.email} size={36} bg={u.role === "admin" ? "var(--info)" : "var(--primary-green)"} />
                                <span style={{ fontWeight: 500, fontSize: 14 }}>{u.name || u.email?.split("@")[0]}</span>
                              </div>
                            </td>
                            <td style={{ fontSize: 14, color: "var(--text-muted)" }}>{u.email}</td>
                            <td><span className="badge badge-info" style={{ background: u.role === "admin" ? "var(--bg-blue)" : "var(--bg-green)", color: u.role === "admin" ? "var(--primary-blue)" : "var(--primary-green)" }}>{u.role}</span></td>
                            <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{fmtDate(u.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showModal && selComplaint && (
        <ComplaintModal complaint={selComplaint} onUpdate={updateComplaint} onClose={() => setShowModal(false)} getUserInfo={getUserInfo} />
      )}
    </div>
  );
}

// ─── Complaint Modal ──────────────────────────────────────────────────────────
function ComplaintModal({ complaint, onUpdate, onClose, getUserInfo }) {
  const [status, setStatus] = useState(complaint.status);
  const [note, setNote] = useState(complaint.admin_note || "");
  const [loading, setLoading] = useState(false);
  const u = getUserInfo(complaint.user_id);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "white", borderRadius: "var(--border-radius-lg)", width: "100%", maxWidth: 520, boxShadow: "var(--shadow-lg)" }}>
        <div className="d-flex justify-content-between align-items-center" style={{ padding: "18px 22px", borderBottom: "1px solid var(--border-color)" }}>
          <span style={{ fontWeight: 600, fontSize: 16 }}>Update Complaint</span>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "var(--text-muted)" }}>&times;</button>
        </div>
        <div style={{ padding: "18px 22px" }}>
          <div style={{ background: "var(--gray-50)", borderRadius: "var(--border-radius-md)", padding: 16, marginBottom: 20 }}>
            <div className="d-flex justify-content-between mb-2">
              <span style={{ fontWeight: 600, fontSize: 15 }}>{complaint.title}</span>
              <UrgencyDot urgency={complaint.urgency} />
            </div>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 12px" }}>{complaint.description}</p>
            <div className="d-flex gap-3" style={{ fontSize: 12, color: "var(--text-muted)" }}>
              <span>👤 {u?.name || "Unknown"}</span>
              <span>📁 {complaint.category}</span>
              {complaint.location && <span>📍 {complaint.location}</span>}
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Update Status</label>
            <div className="d-flex gap-2">
              {["Submitted", "In Progress", "Resolved"].map(s => (
                <button key={s} type="button" onClick={() => setStatus(s)} style={{
                  flex: 1, padding: "10px", border: `1px solid ${status === s ? "var(--primary-blue)" : "var(--border-color)"}`,
                  borderRadius: "var(--border-radius-sm)", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "var(--font-family)",
                  background: status === s ? "var(--bg-blue)" : "white", color: status === s ? "var(--primary-blue)" : "var(--text-secondary)",
                  transition: "var(--transition-fast)"
                }}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="form-label">Admin Note (sent to student)</label>
            <textarea className="form-control" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Optional message for student..." />
          </div>
        </div>
        <div className="d-flex justify-content-end gap-2" style={{ padding: "16px 22px", borderTop: "1px solid var(--border-color)" }}>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={loading} onClick={async () => {
            setLoading(true);
            await onUpdate(complaint.id, status, complaint.user_id, note);
            setLoading(false);
          }}>
            {loading ? "Saving..." : "💾 Save & Notify Student"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check auth session
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        console.error("Session fetch error:", error);
      } else if (session?.user) {
        try {
          const { data: prof } = await supabase.from("users").select("*").eq("id", session.user.id).maybeSingle();
          setAuthUser(session.user);
          setProfile(prof);
        } catch (e) {
          console.error("Profile fetch error:", e);
        }
      }
      setLoading(false);
    }).catch(err => {
      console.error("Unexpected auth error:", err);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        const { data: prof } = await supabase.from("users").select("*").eq("id", session.user.id).maybeSingle();
        setAuthUser(session.user);
        setProfile(prof);
      } else {
        setAuthUser(null);
        setProfile(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = (user, prof) => { setAuthUser(user); setProfile(prof); };

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-light)", fontFamily: "var(--font-family)" }}>
      <div className="text-center">
        <img src={LOGO_URL} alt="logo" style={{ width: 60, height: 60, borderRadius: 12, marginBottom: 16 }} />
        <div style={{ fontSize: 14, color: "#9e9e9e", fontWeight: 500 }}>Loading Saylani Portal...</div>
      </div>
    </div>
  );

  if (!authUser) return <LoginPage onLogin={handleLogin} />;
  return profile?.role === "admin"
    ? <AdminDashboard user={authUser} profile={profile} />
    : <StudentDashboard user={authUser} profile={profile} />;
}